/**
 * Instructor Schedule View
 * Shows the logged-in instructor's own assigned sections from the latest run.
 * Accessible to: Scheduler.Instructor (and Scheduler.Admin)
 */

import { useState, useEffect } from 'react';
import { schedulerApi, InstructorScheduleResponse, getSlotLabel } from '../../services/scheduler-api';

const CARD = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '12px',
  padding: '1.5rem',
} as const;

export function InstructorScheduleView({ asEmail }: { asEmail?: string } = {}) {
  const [data, setData] = useState<InstructorScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = asEmail
      ? schedulerApi.getInstructorScheduleAs(asEmail)
      : schedulerApi.getInstructorSchedule();
    fetch
      .then(setData)
      .catch(e => setError(e?.response?.data?.message || e?.message || 'Could not load schedule'))
      .finally(() => setLoading(false));
  }, [asEmail]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-primary)' }}>
        Loading your schedule…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...CARD, color: '#f87171', textAlign: 'center', padding: '2rem' }}>
        ❌ {error}
      </div>
    );
  }

  const sections = data?.sections ?? [];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, color: 'var(--accent)', fontSize: '28px' }}>📋 My Schedule</h2>
        <p style={{ margin: '0.4rem 0 0 0', color: 'var(--text-secondary)' }}>
          {data?.instructor && `${data.instructor} — `}
          {data?.term_start && data?.term_end
            ? `Term: ${data.term_start} – ${data.term_end}`
            : 'Your assigned sections for the current term'}
        </p>
      </div>

      {/* Message if no schedule published yet */}
      {data?.message && sections.length === 0 && (
        <div style={{ ...CARD, textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📭</div>
          <p>{data.message}</p>
        </div>
      )}

      {/* No sections assigned */}
      {!data?.message && sections.length === 0 && (
        <div style={{ ...CARD, textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📭</div>
          <p>No sections have been assigned to you in the current schedule.</p>
        </div>
      )}

      {/* Sections cards */}
      {sections.length > 0 && (
        <>
          <div style={{ marginBottom: '1rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
            {sections.length} section{sections.length !== 1 ? 's' : ''} assigned
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
            {sections.map(s => (
              <div key={s.section_id} style={CARD}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0, fontSize: '20px', color: 'var(--accent)', fontWeight: '700' }}>
                    {s.course.toUpperCase()}
                  </h3>
                  <span style={{
                    fontSize: '11px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    background: s.enrolled >= s.capacity ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.15)',
                    color: s.enrolled >= s.capacity ? '#f87171' : '#4ade80',
                    fontWeight: '600',
                  }}>
                    {s.enrolled} / {s.capacity}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <div>🕐 <strong style={{ color: 'var(--text-primary)' }}>{getSlotLabel(s.slot)}</strong></div>
                  {s.start && s.end && (
                    <div>⏱️ {s.start} – {s.end}</div>
                  )}
                  <div>👥 {s.enrolled} student{s.enrolled !== 1 ? 's' : ''} enrolled</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
